/**
 * Light show diagnostics (ENG-2291), shown by DebugOverlay when the show page
 * is opened with `?lightshowdebug` (`?lightshowdebug=0` turns it off again).
 * Parts of the light show record what they do here; the overlay reads it a
 * few times a second. Nothing is recorded unless debugging is on.
 */

const PARAM = "lightshowdebug";

const readFlag = () => {
  if (typeof window === "undefined") return false;
  try {
    const value = new URLSearchParams(window.location.search).get(PARAM);
    // Kept for the session, so it survives navigating around the show page.
    if (value === "0") window.sessionStorage.removeItem(PARAM);
    else if (value !== null) window.sessionStorage.setItem(PARAM, "1");
    return window.sessionStorage.getItem(PARAM) === "1";
  } catch (e) {
    return false;
  }
};

export const isLightShowDebug = readFlag();

/** Times are on the server clock (ms), as the phone estimates it. */
/** A live change's arrival over Ably. */
export interface DebugArrival {
  lookName?: string;
  /** When the show runner made the change. */
  startedAtMs?: number;
  /** When phones should apply it (startedAt + startDelayMs). */
  startAtMs?: number;
  receivedAtMs: number;
}

/** One received live change, kept in order for load tests. */
export interface DebugArrivalRecord extends DebugArrival {
  /** How it arrived. */
  source: "ably" | "firestore";
  /** Local clock when it arrived (ms). */
  localMs: number;
}

export interface LightShowDebug {
  /**
   * Every live change received, oldest first (capped). Load tests read it
   * from `window.__lightShow` (see lightshow-loadtest).
   */
  arrivals: DebugArrivalRecord[];
  /** Local time the page loaded, to tell changes made before it apart. */
  openedAt: number;
  clock?: {
    offsetMs: number;
    roundTripMs: number;
    /** Local time of the last successful sync. */
    syncedAt: number;
    failedSamples: number;
  };
  ably?: {
    /** The Ably connection state, e.g. "connected". */
    state?: string;
    /** The id this phone connects with: its Firebase uid. */
    clientId?: string;
    /** The light show channel's state, e.g. "attached". */
    channel?: string;
    /** The last connection or channel error, e.g. "40142 Token expired". */
    lastError?: string;
    messages?: number;
    lastMessage?: DebugArrival;
  };
  lastApply?: {
    lookName?: string;
    scheduledMs: number;
    appliedMs: number;
  };
  /** Recent frame-to-frame times in the takeover (ms). */
  frames: number[];
  fanState?: string;
  /** Recent Ably client log lines, newest last. */
  ablyLogs: string[];
  wakeLock?: "held" | "released" | "denied" | "unsupported";
}

export const lightShowDebug: LightShowDebug = {
  openedAt: Date.now(),
  arrivals: [],
  ablyLogs: [],
  frames: [],
};

const MAX_FRAMES = 120;
const MAX_ARRIVALS = 1000;

export const recordArrival = (arrival: DebugArrivalRecord) => {
  if (!isLightShowDebug) return;
  const { arrivals } = lightShowDebug;
  arrivals.push(arrival);
  if (arrivals.length > MAX_ARRIVALS) arrivals.shift();
};

// Load tests drive many pages and read what each one received.
if (isLightShowDebug) {
  (window as Window & { __lightShow?: LightShowDebug }).__lightShow =
    lightShowDebug;
}

export const recordFrame = (deltaMs: number) => {
  if (!isLightShowDebug) return;
  const { frames } = lightShowDebug;
  frames.push(deltaMs);
  if (frames.length > MAX_FRAMES) frames.shift();
};

export const recordDebug = (update: Partial<LightShowDebug>) => {
  if (!isLightShowDebug) return;
  Object.assign(lightShowDebug, update);
};

const MAX_ABLY_LOGS = 50;

/** Ably's logHandler in debug mode: keeps recent lines, and logs them too. */
export const recordAblyLog = (message: string) => {
  const { ablyLogs } = lightShowDebug;
  const time = new Date().toISOString().slice(11, 23);
  ablyLogs.push(`${time} ${message}`);
  if (ablyLogs.length > MAX_ABLY_LOGS) ablyLogs.shift();
  console.log("[ably]", message);
};
